import axios from "axios";
import moment from "moment";
import { getTokenHeader } from "@/helpers/commonHelper";
import { num, pct } from "@/utils/format";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
const BASE = `${env.prompts_api_client_url}api/eo-prompt`;
const OVERVIEW_SAMPLE_LIMIT = 250;
const ALL_SERVICES_VALUE = "__all_services__";
const ALL_JOBS_VALUE = "__all_jobs__";

const METRIC_LABELS = {
  memoryRssMinBytes: "Arbeitsspeicher min.",
  memoryRssMaxBytes: "Arbeitsspeicher max.",
  heapUsedMinBytes: "Heap-Nutzung min.",
  heapUsedMaxBytes: "Heap-Nutzung max.",
  cpuUserMicros: "CPU-Zeit User",
  cpuSystemMicros: "CPU-Zeit System"
};

const createDefaultOverview = () => ({
  totalRuns: 0,
  successfulRuns: 0,
  failedRuns: 0,
  avgDurationMs: 0,
  lastRunAt: ""
});

export default {
  name: "CronJobManagement",
  data() {
    return {
      activeTab: 0,
      selectedRangeKey: "month",
      rangeOptions: [
        { text: "Heute", value: "today" },
        { text: "7 Tage", value: "7days" },
        { text: "Monat", value: "month" },
        { text: "12 Monate", value: "12months" }
      ],
      loadingOverview: false,
      loadingJobs: false,
      loadingFilters: false,
      errorText: "",
      overview: createDefaultOverview(),
      topJobs: [],
      recentFailures: [],
      filterOptions: {
        serviceNames: [{ text: "Alle Services", value: ALL_SERVICES_VALUE }],
        jobNames: [{ text: "Alle Jobs", value: ALL_JOBS_VALUE }]
      },
      statusOptions: [
        { text: "Alle Status", value: "all" },
        { text: "Erfolgreich", value: "true" },
        { text: "Fehlgeschlagen", value: "false" }
      ],
      globalFilters: {
        serviceName: ALL_SERVICES_VALUE,
        jobName: ALL_JOBS_VALUE,
        success: "all"
      },
      jobFilters: {
        fromDate: "",
        toDate: ""
      },
      jobs: [],
      expandedJobs: [],
      tableState: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 1,
        sortBy: "startedAt",
        sortOrder: "desc"
      },
      limitOptions: [10, 20, 50, 100],
      overviewIssueHeaders: [
        { text: "Job", value: "jobName", sortable: false, width: "62%" },
        { text: "Läufe", value: "runs", sortable: false, align: "right" },
        {
          text: "Fehler",
          value: "failures",
          sortable: false,
          align: "right"
        }
      ],
      executionHeaders: [
        { text: "Service", value: "serviceName", sortable: false },
        { text: "Job", value: "jobName", sortable: false },
        { text: "Ausführung", value: "schedule", sortable: false },
        { text: "Letzter Lauf", value: "lastStartedAt", sortable: false }
      ],
      executionInfos: [],
      jobHeaders: [
        {
          text: "Service",
          value: "serviceName",
          sortable: false,
          width: "16%"
        },
        { text: "Job", value: "jobName", sortable: false, width: "20%" },
        { text: "Start", value: "startedAt", sortable: false, width: "14%" },
        { text: "Dauer", value: "durationMs", sortable: false, width: "10%" },
        { text: "Status", value: "success", sortable: false, width: "12%" },
        { text: "Summary", value: "summary", sortable: false, width: "28%" }
      ]
    };
  },
  computed: {
    failureRateLabel() {
      if (!this.overview.totalRuns) return pct(0);
      return pct((this.overview.failedRuns / this.overview.totalRuns) * 100);
    },
    overviewMetaLine() {
      return `${num(this.overview.totalRuns)} Läufe · ${num(
        this.overview.failedRuns
      )} Fehler`;
    },
    selectedRangeLabel() {
      const found = this.rangeOptions.find(
        option => option.value === this.selectedRangeKey
      );
      return found?.text || "Zeitraum";
    },
    hasActiveGlobalFilters() {
      return (
        this.globalFilters.serviceName !== ALL_SERVICES_VALUE ||
        this.globalFilters.jobName !== ALL_JOBS_VALUE ||
        this.globalFilters.success !== "all"
      );
    }
  },
  async mounted() {
    this.applyRangePreset(this.selectedRangeKey);
    await this.loadPage();
  },
  methods: {
    async loadPage() {
      this.errorText = "";
      await Promise.all([
        this.fetchFilterOptions(),
        this.fetchOverviewMetrics(),
        this.fetchJobs()
      ]);
    },
    async fetchFilterOptions() {
      this.loadingFilters = true;
      try {
        const headers = getTokenHeader() || {};
        const response = await axios.get(`${BASE}/crons/filters`, { headers });

        const serviceNames = [...new Set(response.data?.serviceNames || [])]
          .filter(Boolean)
          .sort((a, b) => String(a).localeCompare(String(b), "de"))
          .map(value => ({ text: value, value }));
        const jobNames = [...new Set(response.data?.jobNames || [])]
          .filter(Boolean)
          .sort((a, b) => String(a).localeCompare(String(b), "de"))
          .map(value => ({ text: value, value }));

        this.filterOptions = {
          serviceNames: [
            { text: "Alle Services", value: ALL_SERVICES_VALUE },
            ...serviceNames
          ],
          jobNames: [{ text: "Alle Jobs", value: ALL_JOBS_VALUE }, ...jobNames]
        };

        if (
          !this.filterOptions.serviceNames.some(
            item => item.value === this.globalFilters.serviceName
          )
        ) {
          this.globalFilters.serviceName = ALL_SERVICES_VALUE;
        }

        if (
          !this.filterOptions.jobNames.some(
            item => item.value === this.globalFilters.jobName
          )
        ) {
          this.globalFilters.jobName = ALL_JOBS_VALUE;
        }
      } catch (error) {
        this.filterOptions = {
          serviceNames: [{ text: "Alle Services", value: ALL_SERVICES_VALUE }],
          jobNames: [{ text: "Alle Jobs", value: ALL_JOBS_VALUE }]
        };
        await this.$store.dispatch("createError", error);
      } finally {
        this.loadingFilters = false;
      }
    },
    async fetchOverviewMetrics() {
      this.loadingOverview = true;

      const from = this.toApiIsoDate(this.jobFilters.fromDate, false);
      const to = this.toApiIsoDate(this.jobFilters.toDate, true);

      try {
        const headers = getTokenHeader() || {};
        const baseParams = {
          from,
          to,
          ...this.buildCommonCronFilters(false)
        };
        const statusFilter = this.globalFilters.success;
        const totalParams =
          statusFilter !== "all"
            ? { ...baseParams, success: statusFilter, page: 1, limit: 1 }
            : { ...baseParams, page: 1, limit: 1 };
        const sampleParams =
          statusFilter !== "all"
            ? {
                ...baseParams,
                success: statusFilter,
                page: 1,
                limit: OVERVIEW_SAMPLE_LIMIT,
                sortBy: "startedAt",
                sortOrder: "desc"
              }
            : {
                ...baseParams,
                page: 1,
                limit: OVERVIEW_SAMPLE_LIMIT,
                sortBy: "startedAt",
                sortOrder: "desc"
              };

        const [allRunsResponse, sampleResponse] = await Promise.all([
          axios.get(`${BASE}/crons`, {
            headers,
            params: totalParams
          }),
          axios.get(`${BASE}/crons`, {
            headers,
            params: sampleParams
          })
        ]);

        let successfulRuns = 0;
        let failedRuns = 0;

        if (statusFilter === "true") {
          successfulRuns = allRunsResponse.data?.pagination?.total || 0;
        } else if (statusFilter === "false") {
          failedRuns = allRunsResponse.data?.pagination?.total || 0;
        } else {
          const [successRunsResponse, failedRunsResponse] = await Promise.all([
            axios.get(`${BASE}/crons`, {
              headers,
              params: { ...baseParams, success: true, page: 1, limit: 1 }
            }),
            axios.get(`${BASE}/crons`, {
              headers,
              params: { ...baseParams, success: false, page: 1, limit: 1 }
            })
          ]);

          successfulRuns = successRunsResponse.data?.pagination?.total || 0;
          failedRuns = failedRunsResponse.data?.pagination?.total || 0;
        }

        const sampleItems = sampleResponse.data?.items || [];

        const durations = sampleItems
          .map(item => Number(item?.durationMs))
          .filter(value => Number.isFinite(value) && value >= 0);

        const avgDurationMs = durations.length
          ? durations.reduce((sum, value) => sum + value, 0) / durations.length
          : 0;

        const groupedJobs = {};
        sampleItems.forEach(item => {
          const key = item?.jobName || "(ohne Namen)";
          if (!groupedJobs[key]) {
            groupedJobs[key] = {
              jobName: key,
              runs: 0,
              failures: 0
            };
          }
          groupedJobs[key].runs += 1;
          if (!item?.success) groupedJobs[key].failures += 1;
        });

        this.topJobs = Object.values(groupedJobs)
          .sort((a, b) => {
            if (b.failures !== a.failures) return b.failures - a.failures;
            return b.runs - a.runs;
          })
          .slice(0, 6);

        this.recentFailures = sampleItems
          .filter(item => item?.success === false)
          .slice(0, 5);

        this.executionInfos = this.buildExecutionInfos(sampleItems);

        this.overview = {
          totalRuns: allRunsResponse.data?.pagination?.total || 0,
          successfulRuns,
          failedRuns,
          avgDurationMs,
          lastRunAt: sampleItems[0]?.startedAt || ""
        };
      } catch (error) {
        this.overview = createDefaultOverview();
        this.topJobs = [];
        this.recentFailures = [];
        this.executionInfos = [];
        await this.$store.dispatch("createError", error);
      } finally {
        this.loadingOverview = false;
      }
    },
    buildExecutionInfos(items = []) {
      const map = new Map();

      items.forEach(item => {
        const serviceName = item?.serviceName || "-";
        const jobName = item?.jobName || "-";
        const schedule = item?.schedule || "-";
        const key = `${serviceName}__${jobName}__${schedule}`;
        const startedAt = item?.startedAt || "";

        if (!map.has(key)) {
          map.set(key, {
            serviceName,
            jobName,
            schedule,
            lastStartedAt: startedAt,
            runs: 0
          });
        }

        const entry = map.get(key);
        entry.runs += 1;
        if (
          startedAt &&
          (!entry.lastStartedAt || startedAt > entry.lastStartedAt)
        ) {
          entry.lastStartedAt = startedAt;
        }
      });

      return [...map.values()].sort((a, b) => {
        const serviceCompare = a.serviceName.localeCompare(b.serviceName, "de");
        if (serviceCompare !== 0) return serviceCompare;
        return a.jobName.localeCompare(b.jobName, "de");
      });
    },
    buildJobsParams() {
      const params = {
        page: this.tableState.page,
        limit: this.tableState.limit,
        sortBy: this.tableState.sortBy,
        sortOrder: this.tableState.sortOrder
      };

      Object.assign(params, this.buildCommonCronFilters(true));

      if (this.jobFilters.fromDate)
        params.from = this.toApiIsoDate(this.jobFilters.fromDate, false);
      if (this.jobFilters.toDate)
        params.to = this.toApiIsoDate(this.jobFilters.toDate, true);

      return params;
    },
    buildCommonCronFilters(includeStatus) {
      const params = {};
      if (
        this.globalFilters.serviceName &&
        this.globalFilters.serviceName !== ALL_SERVICES_VALUE
      ) {
        params.serviceName = this.globalFilters.serviceName;
      }
      if (
        this.globalFilters.jobName &&
        this.globalFilters.jobName !== ALL_JOBS_VALUE
      ) {
        params.jobName = this.globalFilters.jobName;
      }
      if (includeStatus && this.globalFilters.success !== "all") {
        params.success = this.globalFilters.success;
      }
      return params;
    },
    buildRangePreset(rangeKey) {
      const now = moment();
      let from = moment(now);
      let to = moment(now).endOf("day");

      if (rangeKey === "today") {
        from = moment(now).startOf("day");
      } else if (rangeKey === "7days") {
        from = moment(now)
          .subtract(6, "days")
          .startOf("day");
      } else if (rangeKey === "month") {
        from = moment(now).startOf("month");
      } else if (rangeKey === "12months") {
        from = moment(now)
          .subtract(1, "year")
          .add(1, "day")
          .startOf("day");
      }

      return {
        fromDate: from.format("YYYY-MM-DD"),
        toDate: to.format("YYYY-MM-DD")
      };
    },
    applyRangePreset(rangeKey) {
      const preset = this.buildRangePreset(rangeKey);
      this.jobFilters.fromDate = preset.fromDate;
      this.jobFilters.toDate = preset.toDate;
    },
    toApiIsoDate(value, endOfDay) {
      if (!value) return undefined;
      const input = String(value);
      if (input.includes("T")) {
        return moment(input).toISOString();
      }
      return endOfDay
        ? moment(input)
            .endOf("day")
            .toISOString()
        : moment(input)
            .startOf("day")
            .toISOString();
    },
    async onRangeChange() {
      this.applyRangePreset(this.selectedRangeKey);
      this.tableState.page = 1;
      await Promise.all([this.fetchOverviewMetrics(), this.fetchJobs()]);
    },
    async fetchJobs() {
      this.loadingJobs = true;
      this.errorText = "";

      try {
        const headers = getTokenHeader() || {};
        const response = await axios.get(`${BASE}/crons`, {
          headers,
          params: this.buildJobsParams()
        });

        this.jobs = response.data?.items || [];

        const pagination = response.data?.pagination || {};
        this.tableState.total = Number(pagination.total || 0);
        this.tableState.pages = Number(pagination.pages || 1);
      } catch (error) {
        this.jobs = [];
        this.tableState.total = 0;
        this.tableState.pages = 1;
        this.errorText =
          error?.response?.data?.error ||
          "Fehler beim Laden der Cronjob-Daten.";
        await this.$store.dispatch("createError", error);
      } finally {
        this.loadingJobs = false;
      }
    },
    async refreshOverview() {
      await this.fetchOverviewMetrics();
    },
    async applyGlobalFilters() {
      this.tableState.page = 1;
      await Promise.all([this.fetchOverviewMetrics(), this.fetchJobs()]);
    },
    async resetGlobalFilters() {
      this.globalFilters = {
        serviceName: ALL_SERVICES_VALUE,
        jobName: ALL_JOBS_VALUE,
        success: "all"
      };
      this.tableState.page = 1;
      await Promise.all([this.fetchOverviewMetrics(), this.fetchJobs()]);
    },
    async setTableSort(sortBy) {
      if (!sortBy) return;
      if (this.tableState.sortBy === sortBy) {
        this.tableState.sortOrder =
          this.tableState.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.tableState.sortBy = sortBy;
        this.tableState.sortOrder =
          sortBy === "serviceName" || sortBy === "jobName" ? "asc" : "desc";
      }

      this.tableState.page = 1;
      await this.fetchJobs();
    },
    getTableSortIcon(sortBy) {
      if (this.tableState.sortBy !== sortBy)
        return "mdi-unfold-more-horizontal";
      return this.tableState.sortOrder === "asc"
        ? "mdi-arrow-up"
        : "mdi-arrow-down";
    },
    async onPageChange(value) {
      this.tableState.page = value;
      await this.fetchJobs();
    },
    async onLimitChange() {
      this.tableState.page = 1;
      await this.fetchJobs();
    },

    formatNumber(value) {
      return num(Number(value || 0));
    },
    formatDateTime(value) {
      if (!value) return "-";
      return moment(value).format("DD.MM.YYYY HH:mm");
    },
    formatDuration(value) {
      const durationMs = Number(value);
      if (!Number.isFinite(durationMs) || durationMs < 0) return "-";
      if (durationMs < 1000) return `${Math.round(durationMs)} ms`;

      const totalSeconds = durationMs / 1000;
      if (totalSeconds < 60)
        return `${totalSeconds.toFixed(totalSeconds < 10 ? 1 : 0)} s`;

      const minutes = Math.floor(totalSeconds / 60);
      const seconds = Math.round(totalSeconds % 60);
      return `${minutes} min ${seconds}s`;
    },
    formatBytes(value) {
      const bytes = Number(value);
      if (!Number.isFinite(bytes) || bytes < 0) return "-";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
      if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
      return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
    },
    formatMicrosAsMs(value) {
      const micros = Number(value);
      if (!Number.isFinite(micros) || micros < 0) return "-";
      return `${(micros / 1000).toFixed(2)} ms`;
    },
    getScheduleLabel(schedule) {
      const raw = String(schedule || "").trim();
      if (!raw) return "Kein Zeitplan hinterlegt";

      const parts = raw.split(/\s+/);
      if (parts.length !== 5) return "Zeitplan";

      const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
      const minuteLabel = minute.padStart(2, "0");
      const weekdayMap = {
        "0": "Sonntag",
        "1": "Montag",
        "2": "Dienstag",
        "3": "Mittwoch",
        "4": "Donnerstag",
        "5": "Freitag",
        "6": "Samstag",
        "7": "Sonntag"
      };

      if (raw === "* * * * *") return "Jede Minute";
      if (
        minute.startsWith("*/") &&
        hour === "*" &&
        dayOfMonth === "*" &&
        month === "*" &&
        dayOfWeek === "*"
      ) {
        return `Alle ${minute.replace("*/", "")} Minuten`;
      }
      if (
        minute === "0" &&
        hour === "*" &&
        dayOfMonth === "*" &&
        month === "*" &&
        dayOfWeek === "*"
      ) {
        return "Stündlich zur vollen Stunde";
      }
      if (
        minute !== "*" &&
        hour.startsWith("*/") &&
        dayOfMonth === "*" &&
        month === "*" &&
        dayOfWeek === "*"
      ) {
        const everyHours = hour.replace("*/", "");
        if (minute === "0") {
          return `Alle ${everyHours} Stunden um :00 (z. B. 00:00, ${everyHours.padStart(
            2,
            "0"
          )}:00, ...)`;
        }
        return `Alle ${everyHours} Stunden zur Minute ${minuteLabel}`;
      }
      if (
        minute !== "*" &&
        hour !== "*" &&
        dayOfMonth === "*" &&
        month === "*" &&
        dayOfWeek === "*"
      ) {
        return `Täglich um ${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
      }
      if (
        minute !== "*" &&
        hour !== "*" &&
        dayOfMonth === "*" &&
        month === "*" &&
        dayOfWeek !== "*"
      ) {
        const weekdayLabel = weekdayMap[dayOfWeek] || `Tag ${dayOfWeek}`;
        return `Wöchentlich am ${weekdayLabel} um ${hour.padStart(
          2,
          "0"
        )}:${minuteLabel}`;
      }

      return "Benutzerdefinierter Cron-Zeitplan";
    },
    toDisplayRows(record = {}) {
      return Object.entries(record || {})
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => ({
          key,
          value: value === null ? "-" : String(value)
        }));
    },
    toDisplayMetricRows(metrics = {}) {
      const rows = [];
      if (!metrics || typeof metrics !== "object") return rows;

      if (metrics.memoryRssMinBytes !== undefined) {
        rows.push({
          key: METRIC_LABELS.memoryRssMinBytes,
          value: this.formatBytes(metrics.memoryRssMinBytes)
        });
      }
      if (metrics.memoryRssMaxBytes !== undefined) {
        rows.push({
          key: METRIC_LABELS.memoryRssMaxBytes,
          value: this.formatBytes(metrics.memoryRssMaxBytes)
        });
      }
      if (metrics.heapUsedMinBytes !== undefined) {
        rows.push({
          key: METRIC_LABELS.heapUsedMinBytes,
          value: this.formatBytes(metrics.heapUsedMinBytes)
        });
      }
      if (metrics.heapUsedMaxBytes !== undefined) {
        rows.push({
          key: METRIC_LABELS.heapUsedMaxBytes,
          value: this.formatBytes(metrics.heapUsedMaxBytes)
        });
      }
      if (metrics.cpuUserMicros !== undefined) {
        rows.push({
          key: METRIC_LABELS.cpuUserMicros,
          value: this.formatMicrosAsMs(metrics.cpuUserMicros)
        });
      }
      if (metrics.cpuSystemMicros !== undefined) {
        rows.push({
          key: METRIC_LABELS.cpuSystemMicros,
          value: this.formatMicrosAsMs(metrics.cpuSystemMicros)
        });
      }

      return rows;
    },
    getStatusColor(success) {
      return success ? "success" : "error";
    },
    getStatusLabel(success) {
      return success ? "Erfolgreich" : "Fehlgeschlagen";
    }
  }
};
